import React from 'react';

// Inline SVG icons — same pattern as Sidebar.tsx

const TrendingUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

const BarChart3Icon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

const ScatterChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="7.5" r="1" />
    <circle cx="18.5" cy="5.5" r="1" />
    <circle cx="11.5" cy="11.5" r="1" />
    <circle cx="7.5" cy="16.5" r="1" />
    <circle cx="15.5" cy="15.5" r="1" />
    <path d="M3 3v18h18" />
  </svg>
);

const CalculatorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="16" y1="14" x2="16" y2="18" />
    <line x1="8" y1="10" x2="8" y2="10.01" />
    <line x1="12" y1="10" x2="12" y2="10.01" />
    <line x1="16" y1="10" x2="16" y2="10.01" />
    <line x1="8" y1="14" x2="8" y2="14.01" />
    <line x1="12" y1="14" x2="12" y2="14.01" />
    <line x1="8" y1="18" x2="8" y2="18.01" />
    <line x1="12" y1="18" x2="12" y2="18.01" />
  </svg>
);

const ActivityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export interface AnalysisCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  subOptions: { id: string; label: string }[];
}

export const ANALYSIS_CATEGORIES: AnalysisCategory[] = [
  {
    id: 'curve_fitting',
    label: 'Curve Fitting',
    icon: <TrendingUpIcon />,
    subOptions: [
      { id: 'dose_response', label: 'Dose-Response (4PL)' },
      { id: 'michaelis_menten', label: 'Michaelis-Menten' },
      { id: 'scatchard', label: 'Scatchard' },
      { id: 'rosenthal', label: 'Rosenthal' },
      { id: 'klotz', label: 'Klotz' },
      { id: 'custom', label: 'Custom Equation' },
    ],
  },
  {
    id: 'compare_groups',
    label: 'Compare Groups',
    icon: <BarChart3Icon />,
    subOptions: [
      { id: 'unpaired_t', label: 'Unpaired t-test' },
      { id: 'paired_t', label: 'Paired t-test' },
      { id: 'welch_t', label: "Welch's t-test" },
      { id: 'mann_whitney', label: 'Mann-Whitney U' },
      { id: 'one_way_anova', label: 'One-way ANOVA' },
      { id: 'kruskal_wallis', label: 'Kruskal-Wallis' },
    ],
  },
  {
    id: 'correlation',
    label: 'Correlation',
    icon: <ScatterChartIcon />,
    subOptions: [
      { id: 'pearson', label: 'Pearson' },
      { id: 'spearman', label: 'Spearman' },
      { id: 'linear_regression', label: 'Linear Regression' },
    ],
  },
  {
    id: 'descriptive',
    label: 'Descriptive',
    icon: <CalculatorIcon />,
    subOptions: [
      { id: 'descriptive', label: 'Summary Stats (auto)' },
    ],
  },
  {
    id: 'spr_kinetics',
    label: 'SPR Kinetics',
    icon: <ActivityIcon />,
    subOptions: [
      { id: 'langmuir_1_1', label: '1:1 Langmuir' },
      { id: 'heterogeneous', label: 'Heterogeneous' },
      { id: 'bivalent', label: 'Bivalent' },
      { id: 'two_state', label: 'Two-State' },
    ],
  },
];

interface SpectraToolbarProps {
  activeCategory: string | null;
  activeSubOption: string | null;
  onSelectCategory: (id: string) => void;
  onSelectSubOption: (id: string) => void;
}

const SpectraToolbar: React.FC<SpectraToolbarProps> = ({
  activeCategory,
  activeSubOption,
  onSelectCategory,
  onSelectSubOption,
}) => {
  const activeCat = ANALYSIS_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      borderBottom: '1px solid var(--card-border)',
      background: 'var(--card-bg)',
    }}>
      {/* Category buttons */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '8px 12px',
        alignItems: 'center',
        overflowX: 'auto',
      }}>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginRight: '8px', whiteSpace: 'nowrap' }}>
          Analysis:
        </span>
        {ANALYSIS_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => onSelectCategory(cat.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '6px',
              border: activeCategory === cat.id ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid var(--card-border)',
              background: activeCategory === cat.id
                ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.15))'
                : 'var(--hover-bg)',
              color: activeCategory === cat.id ? 'rgb(129, 120, 248)' : 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: activeCategory === cat.id ? 600 : 400,
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
            }}
          >
            <span style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center' }}>
              {cat.icon}
            </span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Sub-options */}
      {activeCat && (
        <div style={{
          display: 'flex',
          gap: '4px',
          padding: '4px 12px 8px',
          alignItems: 'center',
          overflowX: 'auto',
          borderTop: '1px solid var(--divider)',
        }}>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginRight: '8px', whiteSpace: 'nowrap' }}>
            Test:
          </span>
          {activeCat.subOptions.map(opt => (
            <button
              key={opt.id}
              onClick={() => onSelectSubOption(opt.id)}
              style={{
                padding: '4px 10px',
                borderRadius: '4px',
                border: activeSubOption === opt.id ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid transparent',
                background: activeSubOption === opt.id ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                color: activeSubOption === opt.id ? 'rgb(129, 120, 248)' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: activeSubOption === opt.id ? 600 : 400,
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpectraToolbar;
